import React, { useState, useEffect } from 'react';
import {
Table,
TableBody,
TableCell,
TableContainer,
TableHead,
TableRow,
Paper,
TextField,
Button,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Tooltip,
Grid,
Typography,
Pagination,
Slide,
Fade,
InputAdornment,
Snackbar,
Alert,
Box,
CircularProgress,
Chip
} from '@mui/material';
import {
Add as AddIcon,
Edit as EditIcon,
Delete as DeleteIcon,
Search as SearchIcon,
Refresh as RefreshIcon,
Visibility as ViewIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TEXT_CONNECTION_ERROR } from '../utilities/Text';
import { APICommodityList, APICommodityStore, APICommodityUpdate, APICommodityDelete, headers } from '../utilities/APIs';
import useCustomToast from '../hooks/useToast';
import axios from 'axios';
import { formatNumberWithComma } from '../utilities/Functions';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../utilities/Permission';

const Commodity = () => {
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

// State
const [loading, setLoading] = useState(true);
const [openAdd, setOpenAdd] = useState(false);
const [openEdit, setOpenEdit] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);
const [currentCommodity, setCurrentCommodity] = useState(null);
const [page, setPage] = useState(1);
const [rowsPerPage, setRowsPerPage] = useState(10);
const [searchTerm, setSearchTerm] = useState('');
const [error, setError] = useState(null);
const [postStatus, setPostStatus] = useState('ADD');
const [updateId, setUpdateId] = useState('');
const showToast = useCustomToast();
const [pageState, setPageState] = useState({
    pageData: [],
});
const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
});

// Form data - matches the required fields
const [formData, setFormData] = useState({
    commodity_id: '',
    description: '',
    grade: '',
    unitPrice: '0'
});

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

// Helper function
const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    fetchData();
}, []);

const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
    const response = await axios.get(APICommodityList, { headers });
    const data = Array.isArray(response.data.data) ? response.data.data.reverse() : [];
    
    setPageState(prev => ({
        ...prev,
        pageData: data,
    }));

    } catch (error) {
    let errorMessage = "Failed to load commodity data";

    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    }
    else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }

    setError(errorMessage);
    console.log(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

// Filter commodities based on search term
const filteredCommodities = pageState.pageData.filter(commodity => {
    const matchesSearch = 
    commodity.commodity_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    commodity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    commodity.grade.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
});

// Pagination
const count = Math.ceil(filteredCommodities.length / rowsPerPage);
const indexOfLastRow = page * rowsPerPage;
const indexOfFirstRow = indexOfLastRow - rowsPerPage;
const currentRows = filteredCommodities.slice(indexOfFirstRow, indexOfLastRow);

const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
};

// Form handling
const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
    ...prev,
    [name]: value
    }));
};

// Dialog handlers
const handleOpenAdd = () => {
    setPostStatus("ADD");
    setFormData({
    commodity_id: '',
    description: '',
    grade: '',
    unitPrice: '0'
    });
    setOpenAdd(true);
};

const handleOpenView = (commodity) => {
    setCurrentCommodity(commodity);
    setOpenView(true);
};

const handleOpenEdit = (commodity) => {
    setFormData({
    commodity_id: commodity.commodity_id,
    description: commodity.description,
    grade: commodity.grade,
    unitPrice: commodity.unitPrice
    });
    setCurrentCommodity(commodity);
    setUpdateId(commodity.id);
    setPostStatus("UPDATE");
    setOpenEdit(true);
};

const handleOpenDelete = (commodity) => {
    setCurrentCommodity(commodity);
    setUpdateId(commodity.id);
    setPostStatus("DELETE");
    setOpenDelete(true);
};

// Prevent closing modal when clicked outside
const handleClose = (event, reason) => {
    if (reason === 'backdropClick') {
        return;
    }
    setOpenAdd(false);
    setOpenEdit(false);
    setOpenView(false);
    setOpenDelete(false);
};

const handleRefresh = () => {
    setLoading(true);
    fetchData();
    setSnackbar({ open: true, message: 'Commodity data refreshed', severity: 'info' });
};

const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
};

const validateForm = () => {
    const errors = [];
    if (!formData.commodity_id.trim()) errors.push('Commodity ID is required');
    if (!formData.description.trim()) errors.push('Description is required');
    if (!formData.grade.trim()) errors.push('Grade is required');
    
    // Unit price validation - allow 0
    if (formData.unitPrice && isNaN(parseFloat(formData.unitPrice))) {
    errors.push('Please enter a valid price');
    }

    if (errors.length > 0) {
    showToast("Validation Error", errors.join('\n'), "warning");
    return false;
    }
    return true;
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
    if (!validateForm()) return;
    }
    
    const formDataToSend = new FormData();
    formDataToSend.append('commodity_id', formData.commodity_id);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('grade', formData.grade);
    formDataToSend.append('unitPrice', formData.unitPrice || '0');
    formDataToSend.append('updateId', updateId);

    try {
    let postRequest;
    if (postStatus === "ADD") {
        postRequest = await axios.post(APICommodityStore, formDataToSend, { headers });
    } else if (postStatus === "UPDATE") {
        postRequest = await axios.post(APICommodityUpdate, formDataToSend, { headers });
    } else if (postStatus === "DELETE") {
        postRequest = await axios.post(APICommodityDelete, formDataToSend, { headers });
    }

    const response = postRequest.data.status;
    const message = postRequest.data.message;

    if (response === "success") {
        handleClose();
        fetchData();
        showToast("Success", message, "success");
    } else {
        showToast("Error", "Unknown Server Error, Record Not Created", "error");
    }
    } catch (error) {
    if (error.response) {
        if (error.response.status === 422) {
        const errors = error.response.data.errors;
        let errorMessages = [];

        for (const field in errors) {
            errorMessages.push(...errors[field]);
        }

        showToast(
            "Validation Error",
            errorMessages.join("\n"),
            "error"
        );
        } else {
        showToast(
            "Error",
            error.response.data.message || "An error occurred",
            "error"
        );
        }
    } else if (error.code === "ERR_NETWORK") {
        showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
    } else {
        showToast("Error", "An unexpected error occurred", "error");
    }
    }
};

// Loading component
if (loading) {
    return (
    <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
        flexDirection="column"
        gap={2}
    >
        <CircularProgress />
        <Typography variant="body1">Loading commodities...</Typography>
    </Box>
    );
}

// Error component
if (error) {
    return (
    <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
        flexDirection="column"
        gap={2}
    >
        <Typography variant="h6" color="error">
        Error Loading Data
        </Typography>
        <Typography variant="body2" color="textSecondary">
        {error}
        </Typography>
        <Button 
        variant="contained" 
        color="primary" 
        onClick={fetchData}
        startIcon={<RefreshIcon />}
        >
        Retry
        </Button>
    </Box>
    );
}

return (
    <div style={{ marginTop: 15, padding: isMobile ? '8px' : '24px' }}>
    <Slide direction="down" in={true} mountOnEnter unmountOnExit>
        <Paper elevation={3} style={{
        padding: isMobile ? '16px' : '24px',
        borderRadius: '12px',
        marginBottom: '24px'
        }}>
        <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item>
            <Typography variant="subtitle1">
                Commodities Total:
                <Chip
                label={formatNumberWithComma(pageState.pageData.length)} 
                color="primary" 
                variant="outlined" 
                style={{ marginLeft: '8px' }}
                />
            </Typography>
            </Grid>
            <Grid item>
            <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                style={{ borderRadius: '8px' }}
            >
                Add Commodity
            </Button>
            </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: '8px' }}>
            <Grid item xs={12} sm={6} md={4}>
            <TextField
                fullWidth
                variant="outlined"
                placeholder="Search commodities..."
                value={searchTerm}
                onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
                }}
                InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                    <SearchIcon />
                    </InputAdornment>
                ),
                style: { borderRadius: '8px' }
                }}
            />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
            <TextField
                fullWidth
                select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                variant="outlined"
                style={{ borderRadius: '8px' }}
                SelectProps={{
                native: true,
                }}
            >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
            </TextField>
            </Grid>
            <Grid item xs={12} md={4} style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Tooltip title="Refresh data">
                <IconButton
                onClick={handleRefresh}
                style={{ backgroundColor: theme.palette.grey[200], borderRadius: '8px' }}
                >
                <RefreshIcon />
                </IconButton>
            </Tooltip>
            </Grid>
        </Grid>
        </Paper>
    </Slide>

    <Fade in={!loading} timeout={500}>
        <div>
        <TableContainer component={Paper} elevation={3} style={{ borderRadius: '12px', marginBottom: '24px' }}>
            <Table>
            <TableHead>
                <TableRow style={{ backgroundColor: theme.palette.primary.main }}>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>#</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Commodity ID</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Grade</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Unit Price</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {currentRows.length > 0 ? (
                currentRows.map((commodity, index) => (
                    <StyledTableRow key={commodity.id}>
                    <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                        {commodity.commodity_id}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2">
                        {commodity.description}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Chip
                        label={commodity.grade}
                        color="primary"
                        variant="outlined"
                        size="small"
                        />
                    </TableCell>
                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                            <Chip
                            label={formatNumberWithComma(commodity.unitPrice)}
                            color="success"
                            variant="outlined"
                            />
                        </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        <Tooltip title="View Details">
                            <ActionButton
                            variant="contained"
                            color="info"
                            size="small"
                            onClick={() => handleOpenView(commodity)}
                            style={{ backgroundColor: '#17a2b8' }}
                            >
                            <ViewIcon fontSize="small" />
                            </ActionButton>
                        </Tooltip>
                        {hasPermission(PERMISSIONS.ALL) && (
                            <>
                            <Tooltip title="Edit">
                                <ActionButton
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleOpenEdit(commodity)}
                                >
                                <EditIcon fontSize="small" />
                                </ActionButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <ActionButton
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleOpenDelete(commodity)}
                                >
                                <DeleteIcon fontSize="small" />
                                </ActionButton>
                            </Tooltip>
                            </>
                        )}
                        </div>
                    </TableCell>
                    </StyledTableRow>
                ))
                ) : (
                <TableRow>
                    <TableCell colSpan={6} style={{ textAlign: 'center', padding: '40px' }}>
                    <Typography variant="subtitle1">
                        {searchTerm ? 'No matching commodities found' : 'No commodity records available'}
                    </Typography>
                    {!searchTerm && (
                        <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAdd}
                        style={{ marginTop: '16px' }}
                        >
                        Add First Commodity
                        </Button>
                    )}
                    </TableCell>
                </TableRow>
                )}
            </TableBody>
            </Table>
        </TableContainer>

        {filteredCommodities.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <Pagination
                count={count}
                page={page}
                onChange={handleChangePage}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
            />
            </div>
        )}
        </div>
    </Fade>

    {/* Add Commodity Dialog */}
    <Dialog 
        open={openAdd} 
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Add New Commodity</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        <form onSubmit={handleSubmit}>
            <Box>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Commodity ID"
                name="commodity_id"
                value={formData.commodity_id}
                autoComplete="off"
                onChange={handleInputChange}
                required
                variant="outlined"
                helperText="Enter unique commodity ID (e.g., MR2026/7)"
                />
            </Grid><br/>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                autoComplete="off"
                onChange={handleInputChange}
                required
                variant="outlined"
                helperText="Enter commodity description (e.g., Milled Rice)"
                />
            </Grid><br/>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Grade"
                name="grade"
                value={formData.grade}
                autoComplete="off"
                onChange={handleInputChange}
                required
                variant="outlined"
                helperText="Enter grade (e.g., G1, G2, Premium)"
                />
            </Grid><br/>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Unit Price"
                name="unitPrice"
                type="number"
                value={formData.unitPrice}
                autoComplete="off"
                onChange={handleInputChange}
                variant="outlined"
                InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                helperText="Enter price amount (default: 0)"
                />
            </Grid>
            </Box>
        </form>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
            Save Commodity
        </Button>
        </DialogActions>
    </Dialog>

    {/* View Commodity Dialog */}
    <Dialog
        open={openView}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Commodity Details</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        {currentCommodity && (
            <Grid container spacing={3}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Commodity ID:
                </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                {currentCommodity.commodity_id}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Description:
                </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                {currentCommodity.description}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Grade:
                </Typography>
                </Box>
                <Box sx={{ ml: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Chip
                    label={currentCommodity.grade}
                    color="primary"
                    variant="outlined"
                />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Unit Price:
                </Typography>
                </Box>
                <Box sx={{ ml: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Chip
                    label={formatNumberWithComma(currentCommodity.unitPrice)}
                    color="success"
                    variant="outlined"
                    size="medium"
                />
                </Box>
            </Grid>
            </Grid>
        )}
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} color="primary" variant="contained">
            Close
        </Button>
        </DialogActions>
    </Dialog>

    {/* Edit Commodity Dialog */}
    <Dialog
        open={openEdit}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Edit Commodity</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Commodity ID"
                name="commodity_id"
                value={formData.commodity_id}
                autoComplete="off"
                onChange={handleInputChange}
                required
                variant="outlined"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                autoComplete="off"
                onChange={handleInputChange}
                required
                variant="outlined"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Grade"
                name="grade"
                value={formData.grade}
                autoComplete="off"
                onChange={handleInputChange}
                required
                variant="outlined"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Unit Price"
                name="unitPrice"
                type="number"
                value={formData.unitPrice}
                autoComplete="off"
                onChange={handleInputChange}
                variant="outlined"
                InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                />
            </Grid>
            </Grid>
        </form>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
            Update Commodity
        </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px', textAlign: 'center' }}>
        <Typography variant="body1" style={{ marginBottom: '16px' }}>
            Are you sure you want to delete this commodity?
        </Typography>
        <Typography variant="h6" color="error" style={{ fontWeight: 600 }}>
            {currentCommodity?.commodity_id}
        </Typography>
        <Typography variant="body2" style={{ marginTop: '8px' }}>
            Description: {currentCommodity?.description}
        </Typography>
        <Typography variant="caption" display="block" style={{ marginTop: '8px' }}>
            This action cannot be undone.
        </Typography>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} variant="outlined" color="primary">
            Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="error" startIcon={<DeleteIcon />}>
            Delete
        </Button>
        </DialogActions>
    </Dialog>

    {/* Snackbar for notifications */}
    <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
    >
        <Alert
        onClose={handleCloseSnackbar}
        severity={snackbar.severity}
        variant="filled"
        sx={{ width: '100%' }}
        >
        {snackbar.message}
        </Alert>
    </Snackbar>
    </div>
);
};

export default Commodity;

// Styled components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
'&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
},
'&:hover': {
    backgroundColor: theme.palette.action.selected,
    transition: 'background-color 0.3s ease',
},
}));

const ActionButton = styled(Button)(({ theme }) => ({
margin: theme.spacing(0.5),
minWidth: 'auto',
}));